import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { Tab, useMatchMedia } from '@enuygun/design-system'
import {
  CarRentalMobileSearchForm,
  CarRentalSearchForm,
  FlightMobileSearchForm,
  FlightSearchForm,
  HotelMobileSearchForm,
  HotelSearchForm,
  sleep,
} from '@enuygun/frontend-components'
import { CarRental, FlightRotated, Hotels } from '@enuygun/icons'
import dayjs from 'dayjs'
import { useTranslation } from 'next-i18next'
import getConfig from 'next/config'
import { useRouter } from 'next/router'

import { CarRentalBlurGuard } from 'components/SearchForms/CarRentalWrapper'
import InlineBetaBadge from 'components/SearchForms/InlineBetaBadge'
import { flightHandleSubmit } from 'components/SearchForms/Submits'
import { getRooms } from 'components/SearchForms/Submits/hotel'
import { wingieCarRentalHandleSubmit } from 'components/SearchForms/Submits/wingieCarRental'
import Affix from 'components/Affix'
import { useAppState } from 'context/AppContext'
import { useIsClient } from 'hooks/useIsClient'
import { useIsMobile } from 'hooks/useIsMobile'
import { getAnalyticsRequestId } from 'utils/analyticsRequestId'
import { buildResidenceCountryOptions, resolveResidenceCountry, type ResidenceCountryOption } from 'utils/residenceCountries'
import {
  getWingiePreviousCategoryBeforeShow,
  getWingieTabIndexFromRef,
  isWingieCarRentalTabEnabled,
  isWingieHotelTabEnabled,
  saveWingiePreviousCategoryBeforeSubmit,
  WINGIE_SEARCH_FORM_TABS,
} from 'utils/searchForm'
import sendTrackerLog from 'utils/tracker'
import { FLY_NEW_ROUNDTRIP_DISPLAY_FOR_INT_FLIGHTS_KEY, getXExperimentsFromDocument } from 'utils/xExperiments'

import { SearchFormAffixWrapper } from './styled'

const { publicRuntimeConfig } = getConfig()
const processEnv = publicRuntimeConfig?.processEnv

interface WingieSearchFormsProps {
  isAffixed?: boolean
  isTablet?: boolean
  /** SSR'da cr-core countries API'den gelen ikamet ülkesi listesi; yoksa Intl fallback kullanılır. */
  residenceCountries?: ResidenceCountryOption[]
  /** Otel autocomplete loglarında sayfayı belirtir (HOT-39290). */
  autocompletePage?: string
}

const WingieSearchForms: FC<WingieSearchFormsProps> = ({ isAffixed, isTablet = false, residenceCountries: residenceCountriesProp, autocompletePage = 'homepage' }) => {
  const { xs, sm, md } = useMatchMedia()
  const isMobile = useIsMobile()
  const isClient = useIsClient()
  const ref = useRef<HTMLDivElement>(null)
  const { t } = useTranslation('common')
  const { query } = useRouter()
  const [affixFixed, setAffixFixed] = useState(false)

  const delay = isMobile ? 400 : 300

  const { endpoint, market, currency, path_locale, language } = useAppState()
  const isHotelTabEnabled = isWingieHotelTabEnabled(language, market)
  const isCarRentalTabEnabled = isWingieCarRentalTabEnabled(language, market)
  const residenceCountries = useMemo(() => (residenceCountriesProp?.length ? residenceCountriesProp : buildResidenceCountryOptions(language)), [language, residenceCountriesProp])
  const defaultResidenceCountry = useMemo(() => resolveResidenceCountry(market, residenceCountries), [market, residenceCountries])
  // x-experiments yalnızca document.cookie'den okunur; SSR/__NEXT_DATA__'ya yazılmaz.
  const [clientXExperiments] = useState<Record<string, string>>(() => (globalThis.window === undefined ? {} : getXExperimentsFromDocument()))

  const newRtExperimentVariant = useMemo(() => {
    const queryVariant = globalThis.window === undefined ? null : new URLSearchParams(globalThis.location.search).get(FLY_NEW_ROUNDTRIP_DISPLAY_FOR_INT_FLIGHTS_KEY)

    return queryVariant || clientXExperiments[FLY_NEW_ROUNDTRIP_DISPLAY_FOR_INT_FLIGHTS_KEY]
  }, [clientXExperiments])

  // ?ref=flight|hotel|car — ilk boyamada doğru sekme (flicker yok)
  const initialRefTab = getWingieTabIndexFromRef(query.ref as string | string[] | undefined, isHotelTabEnabled, isCarRentalTabEnabled)
  const [activeTab, setActiveTab] = useState<number>(() => (initialRefTab !== -1 ? initialRefTab : WINGIE_SEARCH_FORM_TABS.flight))
  /** URL/localStorage sekme seçimi yalnızca bir kez; aksi halde tab tıklamasını geri zorlar. */
  const appliedInitialTabRef = useRef(false)

  useEffect(() => {
    if (appliedInitialTabRef.current) return

    const refTab = getWingieTabIndexFromRef(query.ref as string | string[] | undefined, isHotelTabEnabled, isCarRentalTabEnabled)
    if (refTab !== -1) {
      appliedInitialTabRef.current = true
      setActiveTab(refTab)
      return
    }

    if (!isClient) return

    appliedInitialTabRef.current = true
    setActiveTab(getWingiePreviousCategoryBeforeShow(true, isHotelTabEnabled, isCarRentalTabEnabled))
  }, [isClient, isHotelTabEnabled, isCarRentalTabEnabled, query.ref])

  /** Tab bileşeni panelKey'i child sırası olarak yorumluyor; kapalı sekmelerde sıra kaymasın diye açık sekmeleri ayrı tutuyoruz. */
  const enabledTabs = useMemo(() => {
    const tabs: number[] = [WINGIE_SEARCH_FORM_TABS.flight]
    if (isHotelTabEnabled) tabs.push(WINGIE_SEARCH_FORM_TABS.hotel)
    if (isCarRentalTabEnabled) tabs.push(WINGIE_SEARCH_FORM_TABS.carRental)
    return tabs
  }, [isHotelTabEnabled, isCarRentalTabEnabled])

  const activeTabPosition = Math.max(enabledTabs.indexOf(activeTab), 0)
  const defaultActivePosition = initialRefTab !== -1 ? Math.max(enabledTabs.indexOf(initialRefTab), 0) : undefined

  const tabNameByKey = useCallback((key: number) => {
    if (key === WINGIE_SEARCH_FORM_TABS.hotel) return 'hotel'
    if (key === WINGIE_SEARCH_FORM_TABS.carRental) return 'car-rental'
    return 'flight'
  }, [])
  const environment = isMobile || isTablet ? 'MOBILE_WEB' : 'DESKTOP_WEB'

  const handleTabClick = useCallback(
    (_e: unknown, key?: string | number) => {
      const toTabIndex = enabledTabs[Number(key) || 0] ?? WINGIE_SEARCH_FORM_TABS.flight
      if (toTabIndex === activeTab) return

      const toTabName = tabNameByKey(toTabIndex)
      const scrollY = window.scrollY

      sendTrackerLog({
        category: 'wingie-homepage',
        page: 'wingie-homepage',
        type: 'search_form_tab_change',
        event: 'search_form_tab_switch',
        message: `${toTabName} tabine tıklandı`,
        environment,
        from_tab_index: activeTab,
        to_tab_index: toTabIndex,
        from_tab_name: tabNameByKey(activeTab),
        to_tab_name: toTabName,
        product_category: toTabName,
        requestId: getAnalyticsRequestId(),
        market: market || 'us',
        language,
        isMobile: !!isMobile || !!isTablet,
      })

      setActiveTab(toTabIndex)
      requestAnimationFrame(() => {
        window.scrollTo(0, scrollY)
      })
    },
    [activeTab, enabledTabs, environment, isMobile, isTablet, language, market, tabNameByKey]
  )

  const handleFlightSubmit = useCallback(
    (form) => {
      saveWingiePreviousCategoryBeforeSubmit(WINGIE_SEARCH_FORM_TABS.flight)
      sendTrackerLog({
        category: 'wingie-homepage',
        page: 'wingie-homepage',
        type: 'search_form_submit',
        event: 'search_form_search_submit',
        message: 'flight tabinde arama yapıldı',
        environment,
        product_category: 'flight',
        requestId: getAnalyticsRequestId(),
        market: market || 'us',
        language,
        isMobile: !!isMobile || !!isTablet,
      })
      return flightHandleSubmit(form, {
        market,
        delay,
        url: endpoint,
        currency: currency?.iso,
        pathLocale: path_locale,
        language,
        newRtExperimentVariant,
      })
    },
    [market, endpoint, currency?.iso, delay, path_locale, language, newRtExperimentVariant, isMobile, isTablet, environment]
  )

  const handleHotelSubmit = useCallback(
    async (form) => {
      saveWingiePreviousCategoryBeforeSubmit(WINGIE_SEARCH_FORM_TABS.hotel)
      sendTrackerLog({
        category: 'wingie-homepage',
        page: 'wingie-homepage',
        type: 'search_form_submit',
        event: 'search_form_search_submit',
        message: 'hotel tabinde arama yapıldı',
        environment,
        product_category: 'hotel',
        requestId: getAnalyticsRequestId(),
        market: market || 'us',
        language,
        isMobile: !!isMobile || !!isTablet,
      })

      const { location, guests, date } = form

      const baseParams = {
        checkInDate: dayjs(date.startDate).format('DD.MM.YYYY'),
        checkOutDate: dayjs(date.endDate).format('DD.MM.YYYY'),
        ref: 'homepage',
      }
      const roomDetail = getRooms(guests)
      const slug = location?.slug || ''
      const countryCode = location?.countryCode || ''

      let redirectPath: string

      if (slug === 'my-location') {
        const params = new URLSearchParams({
          ...baseParams,
          roomDetail,
          lat: location.coordinate.latitude.toString(),
          lon: location.coordinate.longitude.toString(),
          p: 'search',
        })
        redirectPath = `/hotels/search/${slug}/?${params}`
      } else if (location?.elastic_type === 'hotel') {
        const params = new URLSearchParams({
          ...baseParams,
          rooms: roomDetail,
          autoRequest: '1',
          country: countryCode,
          p: 'hotel_detail',
          is_single_search: '1',
        })
        redirectPath = `/hotels/detail/${slug}/?${params}`
      } else {
        const params = new URLSearchParams({
          ...baseParams,
          roomDetail,
          country: countryCode,
          p: 'search',
        })
        redirectPath = `/hotels/search/${slug}/?${params}`
      }

      await sleep(delay)
      globalThis.location.href = `${endpoint ?? ''}${redirectPath}`
    },
    [endpoint, delay, market, language, isMobile, isTablet, environment]
  )

  const handleCarRentalSubmit = useCallback(
    async (form) => {
      saveWingiePreviousCategoryBeforeSubmit(WINGIE_SEARCH_FORM_TABS.carRental)
      sendTrackerLog({
        category: 'wingie-homepage',
        page: 'wingie-homepage',
        type: 'search_form_submit',
        event: 'search_form_search_submit',
        message: 'car-rental tabinde arama yapıldı',
        environment,
        product_category: 'car-rental',
        requestId: getAnalyticsRequestId(),
        market: market || 'us',
        language,
        isMobile: !!isMobile || !!isTablet,
      })

      await wingieCarRentalHandleSubmit(form, endpoint ?? '', delay, currency?.iso, market)
    },
    [endpoint, delay, currency?.iso, market, language, isMobile, isTablet, environment]
  )

  const isSmallScreen = isMobile || isTablet || xs || sm || md

  const getPathSegment = (market: string | undefined) => {
    if (market === 'es') {
      return 'vuelos/buscar'
    }
    return 'flights/search'
  }

  const pathSegment = getPathSegment(market)
  const $url = `${endpoint ?? ''}/${pathSegment}/trip-autocomplete/`
  const FlightSearch = isSmallScreen ? FlightMobileSearchForm : FlightSearchForm
  const HotelSearch = isSmallScreen ? HotelMobileSearchForm : HotelSearchForm
  const CarRentalSearch = isSmallScreen ? CarRentalMobileSearchForm : CarRentalSearchForm
  const effectiveIsAffixed = isMobile ? false : isAffixed ?? affixFixed

  const flightForm = <FlightSearch url={$url} onSubmit={handleFlightSubmit} multi={false} isTablet={isTablet} />
  const hotelForm = <HotelSearch title="" onSubmit={handleHotelSubmit} isTablet={isTablet} autocompletePage={autocompletePage} />
  const carRentalForm = (
    <CarRentalBlurGuard>
      <CarRentalSearch
        onSubmit={handleCarRentalSubmit}
        isTablet={isTablet}
        dataTestId="wingie-car-rental"
        showCountryOfResidence
        residenceCountries={residenceCountries}
        defaultValues={{ countryOfResidence: defaultResidenceCountry }}
      />
    </CarRentalBlurGuard>
  )

  const isCarRentalTabActive = activeTab === WINGIE_SEARCH_FORM_TABS.carRental
  const carRentalTabTitle = (
    <span style={{ display: 'inline-flex', alignItems: 'center' }}>
      {t('carRentalSearchForm.tabTitle')}
      <InlineBetaBadge text={t('newBadge')} isActive={isCarRentalTabActive} isMobile={isSmallScreen} dataTestId="wingie-car-rental-tab-new-badge" />
    </span>
  )

  const tabPanels = enabledTabs.map((tabIndex) => {
    if (tabIndex === WINGIE_SEARCH_FORM_TABS.hotel) {
      return {
        tabIndex,
        form: hotelForm,
        title: t('hotelSearchForm.tabTitle'),
        ariaLabel: t('hotelSearchForm.tabTitle'),
        icon: <Hotels style={{ marginInlineEnd: '8px' }} width={20} height={20} />,
      }
    }

    if (tabIndex === WINGIE_SEARCH_FORM_TABS.carRental) {
      return {
        tabIndex,
        form: carRentalForm,
        title: carRentalTabTitle,
        ariaLabel: t('carRentalSearchForm.tabTitle'),
        icon: <CarRental style={{ marginInlineEnd: '8px' }} width={20} height={20} />,
      }
    }

    return {
      tabIndex,
      form: flightForm,
      title: t('flightSearchForm.tabTitle'),
      ariaLabel: t('flightSearchForm.tabTitle'),
      icon: <FlightRotated style={{ marginInlineEnd: '4px' }} width={20} height={20} />,
    }
  })

  const isSingleTab = tabPanels.length === 1

  // Tek sekme kalan marketlerde Tab kullanma: separated-panels 16px padding canlı flight-only görünümü bozar
  return (
    <div ref={ref}>
      <Affix containerRef={ref} market={market} onFixedChange={setAffixFixed}>
        <SearchFormAffixWrapper
          isAffixed={effectiveIsAffixed}
          isWingie={true}
          market={market}
          language={language}
          $flightOnly={isSingleTab}
          $tabCount={tabPanels.length}
          data-testid={`${effectiveIsAffixed ? 'wingie-homepage-stickyForm' : 'wingie-homepage-cardForm'}`}
          data-sticky-form-only={effectiveIsAffixed && !isSingleTab ? true : undefined}
          isTablet={isTablet}
        >
          {isSingleTab ? (
            <div data-testid="wingie-search-form">{flightForm}</div>
          ) : (
            <Tab
              shape="separated"
              defaultActive={defaultActivePosition}
              active={activeTabPosition}
              onClick={handleTabClick}
              dataTestId="wingie-search-form"
              isMobile={isSmallScreen}
              isTablet={isTablet}
            >
              {tabPanels.map((panel, position) => (
                <Tab.Panel
                  key={panel.tabIndex}
                  panelKey={position}
                  icon={panel.icon}
                  unmount={false}
                  justify="flex-center"
                  title={panel.title}
                  ariaLabel={panel.ariaLabel}
                  testIdKey={tabNameByKey(panel.tabIndex)}
                >
                  {panel.form}
                </Tab.Panel>
              ))}
            </Tab>
          )}
        </SearchFormAffixWrapper>
      </Affix>
    </div>
  )
}

export default WingieSearchForms
